import React, { useEffect, useMemo, useState } from "react";
import ErrorState from "@/components/landingPage/common/ErrorState";
import NoPublishedContentState from "@/components/landingPage/common/NoPublishedContentState";
import LandingPageRenderer from "@/components/landingPage/common/LandingPageRenderer";
import { usePreferredLanguage } from "@/hooks/usePreferredLanguage";
import { usePublishedVersion } from "@/components/landingPage/hooks/usePublishedVersion";
import { usePublishedVersionBySubdomain } from "@/components/landingPage/hooks/usePublishedVersionBySubdomain";
import { useCustomDomain } from "@/hooks/useCustomDomain";
import { useSubdomain } from "@/hooks/useSubdomain";
import type { Language } from "@/types/theme";
import type { SiteConfig } from "@/types/sites";
import type { AnalyticsSettings } from "@/analytics";
import { useAppLoader } from "@/contexts/AppLoaderContext";
import SEOHead from "@/seo/SEOHead";
import {
  buildCanonicalFromWindow,
  buildAlternateLanguagesForWindow,
} from "@/seo/convert";
import type { SEOSection } from "@/types/sections/seo";
import { useAnalytics } from "@/analytics";
import { CookieConsentPanel } from "@/components/shared/CookieConsentPanel";
import { setConsent } from "@/analytics/consent";
import { defaultAnalyticsConfig } from "../config";

export type LandingSource =
  | { type: "site"; siteId: string }
  | { type: "customDomain" }
  | { type: "subdomain"; subdomain?: string | null };

interface LandingPageContainerProps {
  source: LandingSource;
  enableTheme?: boolean;
  enableDarkMode?: boolean;
  preventGlobalDarkMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  isPreviewMode?: boolean;
}

const mergeAnalyticsSettings = (
  settings?: SiteConfig["analytics"]
): AnalyticsSettings => ({
  ...defaultAnalyticsConfig,
  ...(settings || {}),
  events: {
    ...defaultAnalyticsConfig.events,
    ...(settings?.events || {}),
  },
});

const SiteSource: React.FC<{
  siteId: string;
  enableTheme?: boolean;
  enableDarkMode?: boolean;
  preventGlobalDarkMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  isPreviewMode?: boolean;
}> = ({
  siteId,
  enableTheme,
  enableDarkMode,
  preventGlobalDarkMode,
  className,
  style,
  children,
  isPreviewMode = false,
}) => {
  const { setContext, track, configure } = useAnalytics();
  const urlParams = new URLSearchParams(window.location.search);
  const urlLanguageParam = urlParams.get("language");
  const { publishedVersion, isLoading, error } = usePublishedVersion(
    siteId,
    urlLanguageParam || undefined
  );
  const defaultLanguageHint = useMemo(
    () =>
      (publishedVersion as any)?.defaultLanguage as Language | undefined,
    [publishedVersion?.defaultLanguage]
  );
  const { language, setLanguage } = usePreferredLanguage(
    siteId,
    urlLanguageParam,
    "en" as Language,
    { defaultLanguage: defaultLanguageHint }
  );

  // Initialize app loading hook
  const { showLoader, hideLoader } = useAppLoader();

  // Control app loader based on loading state
  useEffect(() => {
    if (isLoading) {
      showLoader("site-landing");
    } else {
      hideLoader("site-landing");
    }
  }, [isLoading, showLoader, hideLoader]);

  const availableLanguages = useMemo(
    () => publishedVersion?.availableLanguages || [language],
    [publishedVersion?.availableLanguages, language]
  );
  const config = useMemo(
    () => publishedVersion?.config as SiteConfig | undefined,
    [publishedVersion]
  );
  const analyticsSettings = useMemo(
    () => mergeAnalyticsSettings(config?.analytics),
    [config?.analytics]
  );
  const [showUnavailable, setShowUnavailable] = useState(false);
  const allContent = useMemo(
    () => publishedVersion?.languages || {},
    [publishedVersion]
  );

  // Ensure the language used by the renderer matches what's actually available
  const effectiveDefaultLanguage = useMemo(() => {
    if (availableLanguages && availableLanguages.length === 1) {
      return availableLanguages[0] as Language;
    }
    if (availableLanguages?.includes(language)) {
      return language as Language;
    }
    return (availableLanguages?.[0] as Language) || (language as Language);
  }, [availableLanguages, language]);

  // Update analytics context and send page_view once when data is ready
  React.useEffect(() => {
    if (!publishedVersion) return;
    setContext({
      site_id: siteId,
      domain_type: "site",
      domain: window.location.host,
      language,
      version_id: (publishedVersion as any).id,
    });
    track.trackPageView();
  }, [publishedVersion?.id, siteId, language]);

  React.useEffect(() => {
    configure(analyticsSettings);
  }, [analyticsSettings, configure]);

  // Avoid flashing the unavailable state: delay showing it until after load settles
  useEffect(() => {
    if (isLoading) {
      setShowUnavailable(false);
      return;
    }
    if (publishedVersion) {
      setShowUnavailable(false);
      return;
    }
    if (error) {
      setShowUnavailable(true);
      return;
    }
    const timer = setTimeout(() => setShowUnavailable(true), 250);
    return () => clearTimeout(timer);
  }, [isLoading, publishedVersion, error]);

  // AppLoader handles the loading display
  if (isLoading) return null;
  if (error) return <ErrorState error={error} />;
  if (showUnavailable) return <NoPublishedContentState />;

  const shouldRenderConsent =
    analyticsSettings.enabled && analyticsSettings.respectConsent !== false && !isPreviewMode;

  return (
    <>
      <LandingPageRenderer
        config={config}
        content={allContent}
        availableLanguages={availableLanguages}
        defaultLanguage={effectiveDefaultLanguage}
        onLanguageChange={(lng) => setLanguage(lng as Language)}
        enableTheme={enableTheme}
        enableDarkMode={enableDarkMode ?? (config?.theme?.darkMode || false)}
        preventGlobalDarkMode={preventGlobalDarkMode}
        className={className}
        style={style}
        isPreviewMode={isPreviewMode}
      >
        {/* SEO Head tags from published content for the effective language */}
        {(() => {
          const pageContent = (allContent as Record<string, any>)[
            effectiveDefaultLanguage
          ];
          const seoFromContent = pageContent?.seo as SEOSection | undefined;
          const seo: SEOSection | undefined = seoFromContent
            ? {
                ...seoFromContent,
                canonicalUrl:
                  seoFromContent.canonicalUrl ?? buildCanonicalFromWindow(),
              }
            : undefined;
          if (seo) {
            seo.alternateLanguages = buildAlternateLanguagesForWindow(
              availableLanguages as string[] | undefined,
              effectiveDefaultLanguage
            );
          }
          return <SEOHead seo={seo} language={effectiveDefaultLanguage} />;
        })()}
        {children}
      </LandingPageRenderer>
      {shouldRenderConsent && (
        <CookieConsentPanel onConsentChange={(granted) => setConsent(granted)} />
      )}
    </>
  );
};

const CustomDomainSource: React.FC<{
  enableTheme?: boolean;
  enableDarkMode?: boolean;
  preventGlobalDarkMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  isPreviewMode?: boolean;
}> = ({
  enableTheme,
  enableDarkMode,
  preventGlobalDarkMode,
  className,
  style,
  children,
  isPreviewMode = false,
}) => {
  const { setContext, track, configure } = useAnalytics();
  const {
    siteId,
    manifest,
    isLoading: domainLoading,
    error: domainError,
  } = useCustomDomain();

  // Initialize app loading hook
  const { showLoader, hideLoader } = useAppLoader();

  const urlParams = new URLSearchParams(window.location.search);
  const urlLanguageParam = urlParams.get("language");

  // Pass only explicit URL param to let hook resolve language from site metadata
  const manifestForSite = useMemo(() => {
    if (manifest && manifest.siteId === siteId) {
      return manifest;
    }
    return null;
  }, [manifest, siteId]);

  const publishedVersionResult = usePublishedVersion(
    siteId || undefined,
    urlLanguageParam || undefined,
    manifestForSite
      ? { manifest: manifestForSite, skipRemoteFetch: true }
      : undefined
  );

  const { publishedVersion, isLoading, error } = publishedVersionResult;
  const defaultLanguageHint = useMemo(
    () =>
      (publishedVersion as any)?.defaultLanguage as Language | undefined,
    [publishedVersion?.defaultLanguage]
  );
  const { language, setLanguage } = usePreferredLanguage(
    siteId || undefined,
    urlLanguageParam,
    "en" as Language,
    { defaultLanguage: defaultLanguageHint }
  );

  // Control app loader based on loading state
  useEffect(() => {
    const isLoadingAny = domainLoading || isLoading;
    if (isLoadingAny) {
      showLoader("custom-domain-landing");
    } else {
      hideLoader("custom-domain-landing");
    }
  }, [domainLoading, isLoading, showLoader, hideLoader]);

  const availableLanguages = useMemo(
    () => publishedVersion?.availableLanguages || [language],
    [publishedVersion?.availableLanguages, language]
  );
  const config = useMemo(
    () => publishedVersion?.config as SiteConfig | undefined,
    [publishedVersion]
  );
  const analyticsSettings = useMemo(
    () => mergeAnalyticsSettings(config?.analytics),
    [config?.analytics]
  );
  const [showUnavailable, setShowUnavailable] = useState(false);
  const allContent = useMemo(
    () => publishedVersion?.languages || {},
    [publishedVersion]
  );
  const effectiveDefaultLanguage = useMemo(() => {
    if (availableLanguages && availableLanguages.length === 1) {
      return availableLanguages[0] as Language;
    }
    if (availableLanguages?.includes(language)) {
      return language as Language;
    }
    return (availableLanguages?.[0] as Language) || (language as Language);
  }, [availableLanguages, language]);

  React.useEffect(() => {
    if (!publishedVersion || !siteId) return;
    setContext({
      site_id: siteId ?? undefined,
      domain_type: "custom",
      domain: window.location.host,
      language,
      version_id: (publishedVersion as any).id,
    });
    track.trackPageView();
  }, [publishedVersion?.id, siteId, language]);

  React.useEffect(() => {
    configure(analyticsSettings);
  }, [analyticsSettings, configure]);

  // Avoid flashing the unavailable state: delay showing it until after load settles
  useEffect(() => {
    if (domainLoading || isLoading) {
      setShowUnavailable(false);
      return;
    }
    if (publishedVersion && siteId) {
      setShowUnavailable(false);
      return;
    }
    if (domainError || error) {
      setShowUnavailable(true);
      return;
    }
    const timer = setTimeout(() => setShowUnavailable(true), 250);
    return () => clearTimeout(timer);
  }, [domainLoading, isLoading, publishedVersion, siteId, domainError, error]);

  // AppLoader handles the loading display
  if (domainLoading || isLoading) return null;
  if (domainError) return <ErrorState error={domainError} />;
  if (error) return <ErrorState error={error} />;
  if (showUnavailable) return <NoPublishedContentState />;

  const shouldRenderConsent =
    analyticsSettings.enabled && analyticsSettings.respectConsent !== false && !isPreviewMode;

  return (
    <>
      <LandingPageRenderer
        config={config}
        content={allContent}
        availableLanguages={availableLanguages}
        defaultLanguage={effectiveDefaultLanguage}
        onLanguageChange={(lng) => setLanguage(lng as Language)}
        enableTheme={enableTheme}
        enableDarkMode={enableDarkMode ?? (config?.theme?.darkMode || false)}
        preventGlobalDarkMode={preventGlobalDarkMode}
        className={className}
        style={style}
        isPreviewMode={isPreviewMode}
      >
        {/* SEO Head tags from published content for the effective language */}
        {(() => {
          const pageContent = (allContent as Record<string, any>)[
            effectiveDefaultLanguage
          ];
          const seoFromContent = pageContent?.seo as SEOSection | undefined;
          const seo: SEOSection | undefined = seoFromContent
            ? {
                ...seoFromContent,
                canonicalUrl:
                  seoFromContent.canonicalUrl ?? buildCanonicalFromWindow(),
              }
            : undefined;
          if (seo) {
            seo.alternateLanguages = buildAlternateLanguagesForWindow(
              availableLanguages as string[] | undefined,
              effectiveDefaultLanguage
            );
          }
          return <SEOHead seo={seo} language={effectiveDefaultLanguage} />;
        })()}
        {children}
      </LandingPageRenderer>
      {shouldRenderConsent && (
        <CookieConsentPanel onConsentChange={(granted) => setConsent(granted)} />
      )}
    </>
  );
};

const SubdomainSource: React.FC<{
  subdomain?: string | null;
  enableTheme?: boolean;
  enableDarkMode?: boolean;
  preventGlobalDarkMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  isPreviewMode?: boolean;
}> = ({
  subdomain: subdomainProp,
  enableTheme,
  enableDarkMode,
  preventGlobalDarkMode,
  className,
  style,
  children,
  isPreviewMode = false,
}) => {
  const { setContext, track, configure } = useAnalytics();
  const subdomainDetected = useSubdomain();
  const subdomain = subdomainProp ?? subdomainDetected;
  const [showUnavailable, setShowUnavailable] = useState(false);

  // Initialize app loading hook
  const { showLoader, hideLoader } = useAppLoader();

  const urlParams = new URLSearchParams(window.location.search);
  const urlLanguageParam = urlParams.get("language");

  /**
   * Language handling strategy:
   * 1. usePublishedVersionBySubdomain handles initial load language:
   *    - Uses URL param if present (?language=xx)
   *    - Otherwise uses site's defaultLanguage from metadata
   * 2. usePreferredLanguage tracks language state for UI updates:
   *    - Syncs with siteId once discovered
   *    - Provides setLanguage for language switcher
   *
   * We pass only urlLanguageParam (not the language state) to avoid race conditions
   * where the hook starts with "en" default before siteId is resolved.
   */
  const [resolvedSiteId, setResolvedSiteId] = useState<string | undefined>(
    undefined
  );
  const { publishedVersion, isLoading, error, siteId } =
    usePublishedVersionBySubdomain(subdomain, urlLanguageParam || undefined);
  const defaultLanguageHint = useMemo(
    () =>
      (publishedVersion as any)?.defaultLanguage as Language | undefined,
    [publishedVersion?.defaultLanguage]
  );
  const { language, setLanguage } = usePreferredLanguage(
    resolvedSiteId,
    urlLanguageParam,
    "en" as Language,
    { defaultLanguage: defaultLanguageHint }
  );

  // Control app loader based on loading state
  useEffect(() => {
    if (isLoading) {
      showLoader("subdomain-landing");
    } else {
      hideLoader("subdomain-landing");
    }
  }, [isLoading, showLoader, hideLoader]);

  useEffect(() => {
    if (siteId && siteId !== resolvedSiteId) {
      setResolvedSiteId(siteId);
    }
  }, [siteId, resolvedSiteId]);

  const availableLanguages = useMemo(
    () => publishedVersion?.availableLanguages || [language],
    [publishedVersion?.availableLanguages, language]
  );
  const config = useMemo(
    () => publishedVersion?.config as SiteConfig | undefined,
    [publishedVersion]
  );
  const analyticsSettings = useMemo(
    () => mergeAnalyticsSettings(config?.analytics),
    [config?.analytics]
  );
  const allContent = useMemo(
    () => publishedVersion?.languages || {},
    [publishedVersion]
  );
  const effectiveDefaultLanguage = useMemo(() => {
    if (availableLanguages && availableLanguages.length === 1) {
      return availableLanguages[0] as Language;
    }
    if (availableLanguages?.includes(language)) {
      return language as Language;
    }
    return (availableLanguages?.[0] as Language) || (language as Language);
  }, [availableLanguages, language]);

  React.useEffect(() => {
    if (!publishedVersion) return;
    setContext({
      site_id: siteId ?? undefined,
      domain_type: "subdomain",
      domain: window.location.host,
      language,
      version_id: (publishedVersion as any).id,
    });
    track.trackPageView();
  }, [publishedVersion?.id, siteId, language]);

  React.useEffect(() => {
    configure(analyticsSettings);
  }, [analyticsSettings, configure]);

  // Avoid flashing the unavailable state: delay showing it until after load settles
  useEffect(() => {
    if (isLoading) {
      setShowUnavailable(false);
      return;
    }
    if (publishedVersion) {
      setShowUnavailable(false);
      return;
    }
    if (error) {
      setShowUnavailable(true);
      return;
    }
    const timer = setTimeout(() => setShowUnavailable(true), 250);
    return () => clearTimeout(timer);
  }, [isLoading, publishedVersion, error]);

  // AppLoader handles the loading display
  if (isLoading) return null;
  if (error) return <ErrorState error={error} />;
  if (showUnavailable) return <NoPublishedContentState />;

  const shouldRenderConsent =
    analyticsSettings.enabled && analyticsSettings.respectConsent !== false && !isPreviewMode;

  return (
    <>
      <LandingPageRenderer
        config={config}
        content={allContent}
        availableLanguages={availableLanguages}
        defaultLanguage={effectiveDefaultLanguage}
        onLanguageChange={(lng) => setLanguage(lng as Language)}
        enableTheme={enableTheme}
        enableDarkMode={enableDarkMode ?? (config?.theme?.darkMode || false)}
        preventGlobalDarkMode={preventGlobalDarkMode}
        className={className}
        style={style}
        isPreviewMode={isPreviewMode}
      >
        {/* SEO Head tags from published content for the effective language */}
        {(() => {
          const pageContent = (allContent as Record<string, any>)[
            effectiveDefaultLanguage
          ];
          const seoFromContent = pageContent?.seo as SEOSection | undefined;
          const seo: SEOSection | undefined = seoFromContent
            ? {
                ...seoFromContent,
                canonicalUrl:
                  seoFromContent.canonicalUrl ?? buildCanonicalFromWindow(),
              }
            : undefined;
          if (seo) {
            seo.alternateLanguages = buildAlternateLanguagesForWindow(
              availableLanguages as string[] | undefined,
              effectiveDefaultLanguage
            );
          }
          return <SEOHead seo={seo} language={effectiveDefaultLanguage} />;
        })()}
        {children}
      </LandingPageRenderer>
      {shouldRenderConsent && (
        <CookieConsentPanel onConsentChange={(granted) => setConsent(granted)} />
      )}
    </>
  );
};

const LandingPageContainer: React.FC<LandingPageContainerProps> = ({
  source,
  enableTheme = true,
  enableDarkMode,
  preventGlobalDarkMode = false,
  className,
  style,
  children,
  isPreviewMode = false,
}) => {
  const renderContent = () => {
    if (source.type === "site") {
      return (
        <SiteSource
          siteId={source.siteId}
          enableTheme={enableTheme}
          enableDarkMode={enableDarkMode}
          preventGlobalDarkMode={preventGlobalDarkMode}
          className={className}
          style={style}
          isPreviewMode={isPreviewMode}
        >
          {children}
        </SiteSource>
      );
    }

    if (source.type === "customDomain") {
      return (
        <CustomDomainSource
          enableTheme={enableTheme}
          enableDarkMode={enableDarkMode}
          preventGlobalDarkMode={preventGlobalDarkMode}
          className={className}
          style={style}
          isPreviewMode={isPreviewMode}
        >
          {children}
        </CustomDomainSource>
      );
    }

    // subdomain
    return (
      <SubdomainSource
        subdomain={source.subdomain}
        enableTheme={enableTheme}
        enableDarkMode={enableDarkMode}
        preventGlobalDarkMode={preventGlobalDarkMode}
        className={className}
        style={style}
        isPreviewMode={isPreviewMode}
      >
        {children}
      </SubdomainSource>
    );
  };

  return renderContent();
};

export default LandingPageContainer;
